import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft,
  Building2,
  User,
  Lock,
  Upload,
  Save,
  Image as ImageIcon,
  Eye,
  EyeOff,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyAccount = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [companyForm, setCompanyForm] = useState({
    name: "",
    manager_first_name: "",
    manager_last_name: "",
    address: "",
    postal_code: "",
    city: "",
    phone: "",
    siret: "",
  });

  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  const [accountEmail, setAccountEmail] = useState("");

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/company/profile`);
      const { company, user: userData } = res.data;
      setCompanyForm({
        name: company.name || "",
        manager_first_name: company.manager_first_name || userData.first_name || "",
        manager_last_name: company.manager_last_name || userData.last_name || "",
        address: company.address || "",
        postal_code: company.postal_code || "",
        city: company.city || "",
        phone: company.phone || "",
        siret: company.siret || "",
      });
      setAccountEmail(userData.email || company.email || "");
      if (company.logo) setCompanyLogo(company.logo);
    } catch (error) {
      toast.error("Erreur lors du chargement du profil");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!companyForm.name.trim()) {
      toast.error("Le nom de l'entreprise est obligatoire");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/company/profile`, companyForm);
      toast.success("Profil mis à jour avec succès");
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error("Le nouveau mot de passe doit contenir au moins 6 caractères");
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setChangingPassword(true);
    try {
      await axios.put(`${API}/company/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      toast.success("Mot de passe modifié avec succès");
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du changement de mot de passe");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Veuillez sélectionner une image (PNG ou JPG)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5 Mo");
      return;
    }
    setUploadingLogo(true);
    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64 = event.target.result;
      try {
        await axios.post(`${API}/company/logo`, { logo: base64 });
        setCompanyLogo(base64);
        toast.success("Logo mis à jour avec succès");
      } catch (error) {
        toast.error("Erreur lors de l'upload du logo");
      } finally {
        setUploadingLogo(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const updateField = (field, value) => {
    setCompanyForm((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="my-account-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/dashboard")}
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold text-slate-900">Mon compte</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Company Information */}
        <Card data-testid="company-info-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-sky-600" />
              Informations de l'entreprise
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="company-name">Nom de l'entreprise *</Label>
              <Input
                id="company-name"
                value={companyForm.name}
                onChange={(e) => updateField("name", e.target.value)}
                className="h-11 mt-1"
                data-testid="input-profile-company-name"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="manager-last-name">Nom du gérant *</Label>
                <Input
                  id="manager-last-name"
                  value={companyForm.manager_last_name}
                  onChange={(e) => updateField("manager_last_name", e.target.value)}
                  placeholder="Dupont"
                  className="h-11 mt-1"
                  data-testid="input-profile-manager-last-name"
                />
              </div>
              <div>
                <Label htmlFor="manager-first-name">Prénom du gérant *</Label>
                <Input
                  id="manager-first-name"
                  value={companyForm.manager_first_name}
                  onChange={(e) => updateField("manager_first_name", e.target.value)}
                  placeholder="Jean"
                  className="h-11 mt-1"
                  data-testid="input-profile-manager-first-name"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="address">Adresse</Label>
              <Input
                id="address"
                value={companyForm.address}
                onChange={(e) => updateField("address", e.target.value)}
                className="h-11 mt-1"
                data-testid="input-profile-address"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="postal-code">Code postal</Label>
                <Input
                  id="postal-code"
                  value={companyForm.postal_code}
                  onChange={(e) => updateField("postal_code", e.target.value)}
                  placeholder="75001"
                  className="h-11 mt-1"
                  data-testid="input-profile-postal-code"
                />
              </div>
              <div>
                <Label htmlFor="city">Ville</Label>
                <Input
                  id="city"
                  value={companyForm.city}
                  onChange={(e) => updateField("city", e.target.value)}
                  placeholder="Paris"
                  className="h-11 mt-1"
                  data-testid="input-profile-city"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">Téléphone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={companyForm.phone}
                  onChange={(e) => updateField("phone", e.target.value)}
                  className="h-11 mt-1"
                  data-testid="input-profile-phone"
                />
              </div>
              <div>
                <Label htmlFor="siret">SIRET</Label>
                <Input
                  id="siret"
                  value={companyForm.siret}
                  onChange={(e) => updateField("siret", e.target.value)}
                  className="h-11 mt-1"
                  data-testid="input-profile-siret"
                />
              </div>
            </div>
            <div>
              <Label>Email de l'entreprise</Label>
              <Input
                value={accountEmail}
                disabled
                className="h-11 mt-1 bg-slate-50 text-slate-500"
                data-testid="input-profile-email-display"
              />
              <p className="text-xs text-slate-400 mt-1">
                L'email du compte ne peut pas être modifié ici
              </p>
            </div>

            <Button
              onClick={handleSaveProfile}
              disabled={saving}
              className="w-full h-11 bg-sky-600 hover:bg-sky-700"
              data-testid="save-profile-btn"
            >
              {saving ? (
                <div className="spinner w-5 h-5 border-white border-t-transparent" />
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer les modifications
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Branding / Logo */}
        <Card data-testid="branding-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-sky-600" />
              Logo de l'entreprise
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <div className="flex-shrink-0">
                {companyLogo ? (
                  <img
                    src={companyLogo}
                    alt="Logo"
                    className="w-24 h-24 rounded-lg object-contain border-2 border-slate-200 p-2 bg-white"
                    data-testid="account-logo-preview"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center bg-slate-50">
                    <ImageIcon className="w-8 h-8 text-slate-400" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm text-slate-600 mb-3">
                  Ce logo apparaîtra dans l'en-tête et sur vos rapports PDF.
                </p>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={handleLogoUpload}
                    className="hidden"
                    data-testid="account-logo-upload-input"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={uploadingLogo}
                    onClick={(e) => {
                      e.preventDefault();
                      e.currentTarget.parentElement.querySelector('input[type="file"]').click();
                    }}
                    data-testid="account-upload-logo-btn"
                  >
                    {uploadingLogo ? (
                      <div className="spinner w-4 h-4 border-sky-600 border-t-transparent mr-2" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    {companyLogo ? "Changer le logo" : "Uploader un logo"}
                  </Button>
                </label>
                <p className="text-xs text-slate-400 mt-2">PNG ou JPG, max 5 Mo</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card data-testid="password-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Lock className="w-5 h-5 text-sky-600" />
              Changer le mot de passe
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="current-password">Mot de passe actuel</Label>
              <div className="relative mt-1">
                <Input
                  id="current-password"
                  type={showCurrentPassword ? "text" : "password"}
                  value={passwordForm.current_password}
                  onChange={(e) =>
                    setPasswordForm((prev) => ({ ...prev, current_password: e.target.value }))
                  }
                  className="h-11 pr-10"
                  data-testid="input-current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="new-password">Nouveau mot de passe</Label>
              <div className="relative mt-1">
                <Input
                  id="new-password"
                  type={showNewPassword ? "text" : "password"}
                  value={passwordForm.new_password}
                  onChange={(e) =>
                    setPasswordForm((prev) => ({ ...prev, new_password: e.target.value }))
                  }
                  className="h-11 pr-10"
                  data-testid="input-new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirmer le nouveau mot de passe</Label>
              <Input
                id="confirm-password"
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) =>
                  setPasswordForm((prev) => ({ ...prev, confirm_password: e.target.value }))
                }
                className="h-11 mt-1"
                data-testid="input-confirm-password"
              />
            </div>
            <Button
              onClick={handleChangePassword}
              disabled={changingPassword}
              variant="outline"
              className="w-full h-11"
              data-testid="change-password-btn"
            >
              {changingPassword ? (
                <div className="spinner w-5 h-5 border-sky-600 border-t-transparent" />
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Modifier le mot de passe
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default MyAccount;
